import LegalLayout, { LegalSection } from "@/pages/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      label="Términos y condiciones"
      title="Las reglas del juego, claras y sin letra pequeña."
      updated="Junio de 2026"
      intro="Estos términos explican cómo funciona la relación entre tu familia y Kiddio cuando compras y usas nuestro reloj y el plan de datos."
    >
      <LegalSection n="01" title="Aceptación de los términos">
        <p>
          Al crear una cuenta o comprar un reloj Kiddio, aceptas estos términos. Si no estás de acuerdo con ellos, por favor no
          utilices el servicio.
        </p>
      </LegalSection>

      <LegalSection n="02" title="Qué es Kiddio">
        <p>
          Kiddio es un reloj GPS para niños con un plan de datos asociado. Permite ver la ubicación del dispositivo, definir zonas
          seguras, hacer y recibir llamadas y mensajes de voz solo con contactos aprobados, y enviar una alerta con el botón SOS.
        </p>
      </LegalSection>

      <LegalSection n="03" title="Tu cuenta">
        <p>
          La cuenta debe crearla y gestionarla una persona adulta (padre, madre o tutor legal), que es responsable de su uso y de la
          información que añade. Inicias sesión de forma segura con Google. Mantén tus credenciales protegidas.
        </p>
      </LegalSection>

      <LegalSection n="04" title="Uso adecuado">
        <p>
          Kiddio está pensado para el cuidado familiar. Te comprometes a usarlo de forma legal y responsable, y a no emplearlo para
          vigilar a personas sin su conocimiento fuera del ámbito de la patria potestad sobre tu hijo menor.
        </p>
      </LegalSection>

      <LegalSection n="05" title="El reloj y el plan (son cosas distintas)">
        <p>
          En Kiddio separamos con claridad dos conceptos: el <strong className="text-[#12263A]">reloj</strong> es un producto que
          compras una sola vez, y el <strong className="text-[#12263A]">plan</strong> es una membresía que mantiene el reloj
          conectado.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#12263A]">Reloj Kiddio</strong>: $120, pago único. Es tuyo para siempre.</li>
          <li><strong className="text-[#12263A]">Plan Kiddio</strong>: $7/mes, facturado por año ($84). Puedes pagarlo en 1, 2 o 4 veces.</li>
        </ul>
        <p>Cuantos menos pagos elijas, mayor es el descuento sobre el plan:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#12263A]">1 pago</strong> (todo el año): 15% de descuento.</li>
          <li><strong className="text-[#12263A]">2 pagos</strong> (cada 6 meses): 8% de descuento.</li>
          <li><strong className="text-[#12263A]">4 pagos</strong> (cada 3 meses): 4% de descuento.</li>
        </ul>
        <p>
          Tu primer pago incluye el reloj más el primer plazo del plan. Los pagos se procesan de forma segura a través de Stripe y
          los precios incluyen los impuestos aplicables cuando corresponda.
        </p>
      </LegalSection>

      <LegalSection n="06" title="Renovación y cancelación del plan">
        <p>
          El plan se factura por año. Si eliges pagarlo en 2 o 4 veces, los plazos siguientes se cobran automáticamente en las
          fechas indicadas (cada 6 o cada 3 meses). Al terminar el año, el plan se renueva salvo que lo canceles desde tu cuenta.
          Puedes cancelar cuando quieras; la cancelación surte efecto al final del periodo ya pagado y no genera nuevos cobros. Te
          avisaremos por correo antes de cada pago o renovación.
        </p>
      </LegalSection>

      <LegalSection n="07" title="Envío y entrega">
        <p>
          Preparamos y enviamos tu reloj tras confirmarse el pago. Te informaremos del estado del envío por correo. Los plazos de
          entrega pueden variar según tu ubicación.
        </p>
      </LegalSection>

      <LegalSection n="08" title="Devoluciones y garantía">
        <p>
          Dispones de un plazo de 14 días desde la recepción para devolver el reloj si no te convence, siempre que esté en buen
          estado. El dispositivo cuenta además con una garantía legal frente a defectos de fabricación. Escríbenos y te guiamos en
          el proceso.
        </p>
      </LegalSection>

      <LegalSection n="09" title="Sobre la ubicación y el botón SOS">
        <p>
          Kiddio es una herramienta de tranquilidad familiar, <strong className="text-[#12263A]">no un dispositivo médico ni un
          servicio de emergencias profesional</strong>. La precisión de la ubicación depende de la cobertura y del entorno, por lo
          que puede variar. Ante una emergencia real, contacta siempre con los servicios de emergencia oficiales.
        </p>
      </LegalSection>

      <LegalSection n="10" title="Propiedad intelectual">
        <p>
          La marca Kiddio, el logo, los textos, el diseño y el software son propiedad de Kiddio. No puedes copiarlos ni usarlos sin
          nuestro permiso.
        </p>
      </LegalSection>

      <LegalSection n="11" title="Limitación de responsabilidad">
        <p>
          Ponemos todo nuestro empeño en que el servicio funcione siempre, pero no podemos garantizar una disponibilidad
          ininterrumpida (por ejemplo, ante fallos de cobertura o de terceros). En la medida permitida por la ley, nuestra
          responsabilidad se limita al importe pagado por el servicio.
        </p>
      </LegalSection>

      <LegalSection n="12" title="Mensajes SMS y códigos de verificación">
        <p>
          Si eliges iniciar sesión con tu teléfono, Kiddio puede enviarte mensajes SMS con
          <strong className="text-[#12263A]"> códigos de verificación de un solo uso</strong>. Al escribir tu número y marcar
          la casilla de consentimiento, aceptas recibir estos mensajes en ese número.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Son mensajes <strong className="text-[#12263A]">transaccionales</strong> (de seguridad), no promocionales: no enviamos publicidad por SMS.</li>
          <li>La <strong className="text-[#12263A]">frecuencia</strong> de mensajes puede variar según tu actividad de inicio de sesión.</li>
          <li>Pueden aplicarse <strong className="text-[#12263A]">tarifas de mensajes y datos</strong> de tu operador.</li>
          <li>Responde <strong className="text-[#12263A]">STOP</strong> para dejar de recibir mensajes y <strong className="text-[#12263A]">AYUDA</strong> (o HELP) para obtener asistencia.</li>
          <li>Si te das de baja de los SMS, podrás seguir accediendo con tu correo o con Google.</li>
          <li>Los operadores no se responsabilizan de mensajes retrasados o no entregados.</li>
        </ul>
        <p>Utilizamos a <strong className="text-[#12263A]">Twilio</strong> como proveedor para el envío de estos SMS.</p>
      </LegalSection>

      <LegalSection n="13" title="Cambios en los términos">
        <p>
          Podemos actualizar estos términos para mejorar el servicio o por motivos legales. Publicaremos la nueva versión con su
          fecha y te avisaremos de los cambios importantes.
        </p>
      </LegalSection>

      <LegalSection n="14" title="Ley aplicable y contacto">
        <p>
          Estos términos se rigen por la legislación aplicable en tu país de residencia como consumidor. Para cualquier consulta,
          escríbenos a <strong className="text-[#12263A]">hola@kiddio.com</strong>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
